/**
 * FikoCMMService - CMM (Coordinate Measuring Machine) Tarama Servisi
 *
 * Mobil kamera ile 3D tarama, point cloud oluşturma ve ürün ağacı entegrasyonu.
 */

import * as THREE from 'three';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { FikoRemoteCamera, ConnectionState, PairingInfo, RemoteCameraInfo, IMUSensorData } from './FikoRemoteCamera';

// Re-export IMUSensorData for backward compatibility
export type { IMUSensorData } from './FikoRemoteCamera';
import { FikoPointCloud, PointCloudData } from './FikoPointCloud';
import { FikoMarkerDetector, DetectedMarker } from './FikoMarkerDetector';
import { FikoDepthEstimator } from './FikoDepthEstimator';
import { FikoVIO, VIOPose, IMURawData, VisualObservation } from './FikoVIO';
import { FikoSfM, SfMPoint } from './FikoSfM';
import { FikoImporterPointCloud, FikoPointCloudGrup, POINT_CLOUD_FORMATS } from '../FikoImporter.PointCloud';

/** Tarama modu */
export type ScanMode = 'marker' | 'markerless' | 'photogrammetry';

/** Tarama durumu */
export type ScanState = 'idle' | 'pairing' | 'connected' | 'scanning' | 'processing' | 'completed';

/** Tarama ayarları */
export interface CMMSettings {
    mode: ScanMode;
    captureInterval: number;      // ms
    minFrameCount: number;        // Minimum frame sayısı
    voxelSize: number;           // mm
    markerSize: number;          // mm
    autoCapture: boolean;        // Otomatik yakalama
    showPreview: boolean;        // Önizleme göster
}

/** Tarama sonucu */
export interface CMMScanResult {
    id: string;
    name: string;
    timestamp: Date;
    frameCount: number;
    pointCloud: THREE.Points | null;
    pointCloudData: PointCloudData | null;
    boundingBox: THREE.Box3 | null;
    duration: number;            // ms
}

/** Tarama istatistikleri */
export interface CMMStats {
    framesCaptured: number;
    pointsCollected: number;
    markersDetected: number;
    scanDuration: number;
    lastFrameTime: number;
}

/**
 * FikoCanlıTaramaGrup - Ürün ağacında gösterilecek tarama grubu
 */
export class FikoCanlıTaramaGrup extends THREE.Group {
    public readonly type = 'FikoCanlıTarama';

    // FikoYT ürün ağacı için gerekli alanlar
    public readonly userData: {
        FikoArayuz: {
            simge: string;
            baslik: string;
            renk: string;
        };
        fikoTarama: {
            scanId: string;
            timestamp: Date;
            pointCount: number;
            boundingBox: THREE.Box3 | null;
        };
    };

    public sagMenu: any[] = [];
    public altAracCubugu: any[] = [];
    public solAracCubugu: any[] = [];

    constructor(scanId: string, name: string = 'Canlı Tarama') {
        super();

        this.name = name;

        this.userData = {
            FikoArayuz: {
                simge: '/simgeler/kamera.webp',
                baslik: name,
                renk: '#00E5FF'
            },
            fikoTarama: {
                scanId: scanId,
                timestamp: new Date(),
                pointCount: 0,
                boundingBox: null
            }
        };

        // Sağ tık menüsü
        this.sagMenu = [
            {
                baslik: 'Taramayı Göster/Gizle',
                simge: 'eye',
                tiklandi: () => {
                    this.visible = !this.visible;
                }
            },
            {
                baslik: 'Export PLY',
                simge: 'download',
                tiklandi: () => {
                    this.exportPLY();
                }
            },
            { tipi: 'Ayraç' },
            {
                baslik: 'Sil',
                simge: 'trash',
                tiklandi: () => {
                    this.parent?.remove(this);
                }
            }
        ];
    }

    /**
     * Point cloud ekle
     */
    addPointCloud(points: THREE.Points): void {
        this.add(points);

        // İstatistikleri güncelle
        const geometry = points.geometry;
        const positionAttr = geometry.getAttribute('position');
        if (positionAttr) {
            this.userData.fikoTarama.pointCount = positionAttr.count;
        }

        // Bounding box hesapla
        geometry.computeBoundingBox();
        this.userData.fikoTarama.boundingBox = geometry.boundingBox;
    }

    /**
     * PLY olarak export et
     */
    exportPLY(): void {
        // Point cloud'u bul
        const pointCloud = this.children.find(c => c instanceof THREE.Points) as THREE.Points;
        if (!pointCloud) {
            console.warn('[CanlıTarama] Point cloud bulunamadı');
            return;
        }

        const geometry = pointCloud.geometry;
        const positions = geometry.getAttribute('position');
        const colors = geometry.getAttribute('color');

        if (!positions) {
            console.warn('[CanlıTarama] Position attribute yok');
            return;
        }

        // PLY header
        let ply = 'ply\n';
        ply += 'format ascii 1.0\n';
        ply += `element vertex ${positions.count}\n`;
        ply += 'property float x\n';
        ply += 'property float y\n';
        ply += 'property float z\n';
        if (colors) {
            ply += 'property uchar red\n';
            ply += 'property uchar green\n';
            ply += 'property uchar blue\n';
        }
        ply += 'end_header\n';

        // Vertex data
        for (let i = 0; i < positions.count; i++) {
            const x = positions.getX(i).toFixed(6);
            const y = positions.getY(i).toFixed(6);
            const z = positions.getZ(i).toFixed(6);

            if (colors) {
                const r = Math.floor(colors.getX(i) * 255);
                const g = Math.floor(colors.getY(i) * 255);
                const b = Math.floor(colors.getZ(i) * 255);
                ply += `${x} ${y} ${z} ${r} ${g} ${b}\n`;
            } else {
                ply += `${x} ${y} ${z}\n`;
            }
        }

        // İndir
        const blob = new Blob([ply], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.name.replace(/\s+/g, '_')}_${Date.now()}.ply`;
        a.click();
        URL.revokeObjectURL(url);
    }
}

/**
 * CMM Tarama Servisi
 */
export class FikoCMMService {
    /** Tarama durumu */
    public readonly state$ = new BehaviorSubject<ScanState>('idle');

    /** Ayarlar */
    public readonly settings$ = new BehaviorSubject<CMMSettings>({
        mode: 'marker',
        captureInterval: 100,
        minFrameCount: 30,
        voxelSize: 2,
        markerSize: 50,
        autoCapture: true,
        showPreview: true
    });

    /** İstatistikler */
    public readonly stats$ = new BehaviorSubject<CMMStats>({
        framesCaptured: 0,
        pointsCollected: 0,
        markersDetected: 0,
        scanDuration: 0,
        lastFrameTime: 0
    });

    /** Tarama sonuçları */
    public readonly results$ = new BehaviorSubject<CMMScanResult[]>([]);

    /** Aktif tarama grubu */
    public readonly activeGroup$ = new BehaviorSubject<FikoCanlıTaramaGrup | null>(null);

    /** Bağlantı durumu (remote camera) */
    public readonly connectionState$ = new BehaviorSubject<ConnectionState>('disconnected');

    /** Eşleştirme bilgisi */
    public readonly pairingInfo$ = new BehaviorSubject<PairingInfo | null>(null);

    /** Hata */
    public readonly error$ = new Subject<string>();

    /** Video stream (PC'de önizleme için) */
    public get videoStream$() {
        return this.remoteCamera.videoStream$;
    }

    /** Kamera bilgisi */
    public get cameraInfo$() {
        return this.remoteCamera.cameraInfo$;
    }

    // Bileşenler
    private remoteCamera: FikoRemoteCamera;
    private pointCloud: FikoPointCloud;
    private markerDetector: FikoMarkerDetector;
    private depthEstimator: FikoDepthEstimator;

    // Durum
    private scanStartTime: number = 0;
    private frameCount: number = 0;
    private subscriptions: Subscription[] = [];

    // Sahne referansı
    private scene: THREE.Scene | null = null;

    // Canlı point cloud görselleştirmesi
    private livePoints: THREE.Points | null = null;
    private liveGeometry: THREE.BufferGeometry | null = null;
    private lastVisualUpdate: number = 0;
    private visualUpdateInterval: number = 200; // Her 200ms'de bir görselleştirme güncelle

    // İlk frame referansı - tüm frame'ler buna göre hizalanır
    private referencepose: { rotation: THREE.Euler; translation: THREE.Vector3 } | null = null;

    // Kamera hareketi takibi
    private lastPose: { rotation: THREE.Euler; translation: THREE.Vector3 } | null = null;
    private totalMotion: number = 0;
    private motionThreshold: number = 5; // mm - anlamlı hareket eşiği
    private stationaryFrames: number = 0; // Hareketsiz frame sayısı

    // VIO (Visual Inertial Odometry) - Profesyonel 6-DoF Pose Estimation
    private vio: FikoVIO;
    private lastVIOPose: VIOPose | null = null;
    private vioEnabled: boolean = true;

    // SfM (Structure from Motion) - Gerçek 3D nokta triangulation
    private sfm: FikoSfM;
    private sfmEnabled: boolean = true;

    // Eski değişkenler backward compatibility için
    private lastIMUData: IMUSensorData | null = null;
    private imuQuaternion: THREE.Quaternion = new THREE.Quaternion();
    private visualQuaternion: THREE.Quaternion = new THREE.Quaternion();
    private fusedQuaternion: THREE.Quaternion = new THREE.Quaternion();
    private imuAlpha: number = 0.3;
    private imuCalibrationOffset: THREE.Quaternion = new THREE.Quaternion();
    private imuCalibrated: boolean = false;

    constructor() {
        this.remoteCamera = new FikoRemoteCamera();
        this.pointCloud = new FikoPointCloud();
        this.markerDetector = new FikoMarkerDetector();
        this.depthEstimator = new FikoDepthEstimator();
        this.vio = new FikoVIO();
        this.sfm = new FikoSfM();

        this.setupSubscriptions();
    }

    /**
     * Subscription'ları ayarla
     */
    private setupSubscriptions(): void {
        // Remote camera state
        this.subscriptions.push(
            this.remoteCamera.state$.subscribe(state => {
                this.connectionState$.next(state);

                if (state === 'connected') {
                    this.state$.next('connected');
                } else if (state === 'pairing') {
                    this.state$.next('pairing');
                } else if (state === 'disconnected' && this.state$.value !== 'idle') {
                    this.state$.next('idle');
                }
            })
        );

        // Pairing info
        this.subscriptions.push(
            this.remoteCamera.pairing$.subscribe(info => {
                this.pairingInfo$.next(info);
            })
        );

        // Frame capture (tarama aktifken)
        this.subscriptions.push(
            this.remoteCamera.frameCapture$.subscribe(frame => {
                if (this.state$.value === 'scanning') {
                    this.processFrame(frame);
                }
            })
        );

        // Errors
        this.subscriptions.push(
            this.remoteCamera.error$.subscribe(error => {
                this.error$.next(error);
            })
        );

        // Telefon'dan gelen tarama kontrol komutları
        this.subscriptions.push(
            this.remoteCamera.scanControl$.subscribe(control => {
                // console.log('[CMM] Telefon\'dan tarama komutu:', control.command);

                switch (control.command) {
                    case 'start':
                        // Telefon'dan tarama başlat
                        if (this.state$.value === 'connected') {
                            this.startScan(control.name || `Telefon Taraması ${new Date().toLocaleTimeString('tr-TR')}`);
                        }
                        break;

                    case 'stop':
                        // Taramayı durdur
                        if (this.state$.value === 'scanning') {
                            this.stopScan();
                        }
                        break;

                    case 'complete':
                        // Taramayı tamamla
                        if (this.state$.value === 'scanning') {
                            this.completeScan();
                        }
                        break;
                }
            })
        );

        // IMU sensör verileri (telefon'dan 30 FPS ile geliyor)
        this.subscriptions.push(
            this.remoteCamera.imuData$.subscribe(imuData => {
                this.processIMUData(imuData);
            })
        );
    }

    /**
     * IMU verilerini VIO sistemine gönder
     *
     * DeviceOrientation + DeviceMotion API verileri:
     * - alpha/beta/gamma: Orientation (derece)
     * - accelerationX/Y/Z: Accelerometer (m/s²)
     * - rotationRateAlpha/Beta/Gamma: Gyroscope (deg/s)
     *
     * VIO sistemi:
     * - Madgwick AHRS ile stabil orientation
     * - IMU preintegration ile pozisyon
     * - ZUPT ile drift düzeltme
     * - Visual correction ile marker-based düzeltme
     */
    private processIMUData(imuData: IMUSensorData): void {
        this.lastIMUData = imuData;

        // VIO'ya IMU verisi gönder
        if (this.vioEnabled) {
            const imuRaw: IMURawData = {
                // Gyroscope (deg/s -> bizim sistemde deg/s olarak tutuluyor, VIO rad/s'ye çevirecek)
                gyroX: imuData.rotationRateAlpha || 0,
                gyroY: imuData.rotationRateBeta || 0,
                gyroZ: imuData.rotationRateGamma || 0,

                // Accelerometer (m/s² with gravity)
                accelX: imuData.accelerationX || 0,
                accelY: imuData.accelerationY || 0,
                accelZ: imuData.accelerationZ || 0,

                // DeviceOrientation (degrees)
                alpha: imuData.alpha,
                beta: imuData.beta,
                gamma: imuData.gamma,

                timestamp: imuData.timestamp
            };

            // VIO işle - 6-DoF pose al
            const vioPose = this.vio.processIMU(imuRaw);
            if (vioPose) {
                this.lastVIOPose = vioPose;

                // Debug log (her 30 frame'de bir)
                if (this.frameCount % 30 === 0 && this.state$.value === 'scanning') {
                    const debug = this.vio.getDebugInfo();
                    // console.log(`[VIO] pos=(${vioPose.position.x.toFixed(0)}, ${vioPose.position.y.toFixed(0)}, ${vioPose.position.z.toFixed(0)})mm, ` +
                    //     `conf=${(vioPose.confidence * 100).toFixed(0)}%, drift=${vioPose.estimatedDrift.toFixed(1)}mm, ` +
                    //     `cal=${debug.isCalibrated ? 'OK' : 'NO'}, ref=${debug.referenceSet ? 'OK' : 'NO'}`);
                }
            }
        }

        // Backward compatibility: Eski IMU quaternion da güncelle
        const alphaRad = THREE.MathUtils.degToRad(imuData.alpha);
        const betaRad = THREE.MathUtils.degToRad(imuData.beta);
        const gammaRad = THREE.MathUtils.degToRad(imuData.gamma);
        const euler = new THREE.Euler(betaRad, alphaRad, -gammaRad, 'ZXY');
        this.imuQuaternion.setFromEuler(euler);

        if (!this.imuCalibrated && this.referencepose) {
            this.calibrateIMU();
        }
    }

    /**
     * IMU'yu visual tracking ile kalibre et
     *
     * İlk marker algılandığında, IMU quaternion'unu
     * visual pose ile hizalamak için offset hesapla.
     */
    private calibrateIMU(): void {
        if (!this.referencepose) return;

        // Visual tracking'den quaternion
        this.visualQuaternion.setFromEuler(this.referencepose.rotation);

        // IMU offset = visual * inverse(imu)
        // Böylece: calibrated_imu = offset * imu = visual
        this.imuCalibrationOffset.copy(this.visualQuaternion);
        const imuInverse = this.imuQuaternion.clone().invert();
        this.imuCalibrationOffset.multiply(imuInverse);

        this.imuCalibrated = true;
        // console.log('[CMM] IMU kalibre edildi (visual tracking ile hizalandı)');
    }

    /**
     * Visual ve IMU verilerini birleştir (Complementary Filter)
     *
     * Sensor fusion formülü:
     * fused = slerp(visual, calibrated_imu, alpha)
     *
     * alpha = 0.0: Sadece visual tracking
     * alpha = 0.3: %70 visual, %30 IMU (önerilen)
     * alpha = 1.0: Sadece IMU
     *
     * Visual tracking: Düşük frekanslı, doğru ama marker kaybolabilir
     * IMU: Yüksek frekanslı, hızlı ama drift yapar
     * Fusion: İkisinin avantajlarını birleştirir
     */
    private fuseWithIMU(visualEuler: THREE.Euler): THREE.Euler {
        // IMU verisi veya kalibrasyon yoksa sadece visual kullan
        if (!this.lastIMUData || !this.imuCalibrated) {
            return visualEuler;
        }

        // Visual quaternion
        this.visualQuaternion.setFromEuler(visualEuler);

        // Kalibre edilmiş IMU quaternion
        const calibratedIMU = this.imuCalibrationOffset.clone().multiply(this.imuQuaternion);

        // Spherical linear interpolation (slerp)
        this.fusedQuaternion.slerpQuaternions(this.visualQuaternion, calibratedIMU, this.imuAlpha);

        // Quaternion'u euler'e çevir
        const fusedEuler = new THREE.Euler().setFromQuaternion(this.fusedQuaternion, 'ZYX');

        return fusedEuler;
    }

    /**
     * Sahneyi ayarla
     */
    public setScene(scene: THREE.Scene): void {
        this.scene = scene;
    }

    /**
     * Eşleştirme başlat
     */
    public async startPairing(): Promise<PairingInfo> {
        this.state$.next('pairing');
        return await this.remoteCamera.startPairing();
    }

    /**
     * Tarama başlat
     */
    public startScan(name: string = 'Tarama'): FikoCanlıTaramaGrup | null {
        if (this.connectionState$.value !== 'connected') {
            this.error$.next('Önce mobil cihaza bağlanın');
            return null;
        }

        // Yeni tarama grubu oluştur
        const scanId = `scan_${Date.now()}`;
        const group = new FikoCanlıTaramaGrup(scanId, name);

        // Ürün ağacına ekle (3D çizimler grubuna)
        if (this.scene) {
            const urunAgaci = (this.scene as any).urunAgaci;
            if (urunAgaci) {
                // "Fiko Çizimler" grubunu bul veya ürün ağacına ekle
                let cizimGrup = urunAgaci.children?.find((c: any) => c.name === 'Fiko Çizimler');
                if (cizimGrup) {
                    cizimGrup.add(group);
                } else {
                    urunAgaci.add(group);
                }
            } else {
                this.scene.add(group);
            }
        }

        this.activeGroup$.next(group);
        this.state$.next('scanning');

        // Point cloud temizle
        this.pointCloud.clear();

        // Referans pozu sıfırla (ilk frame'de yeniden ayarlanacak)
        this.referencepose = null;
        this.lastPose = null;
        this.totalMotion = 0;
        this.stationaryFrames = 0;

        // IMU kalibrasyonunu sıfırla (yeni taramada yeniden kalibre edilecek)
        this.imuCalibrated = false;
        this.imuCalibrationOffset.identity();
        this.lastIMUData = null;

        // VIO'yu sıfırla (yeni tarama için temiz başlangıç)
        this.vio.fullReset();
        this.lastVIOPose = null;
        // console.log('[VIO] Sistem sıfırlandı - yeni tarama için hazır');

        // SfM'i sıfırla
        this.sfm.clear();
        // console.log('[SfM] Sistem sıfırlandı - feature tracking hazır');

        // Canlı görselleştirme için point cloud oluştur
        this.createLivePointCloud(group);

        // İstatistikleri sıfırla
        this.scanStartTime = Date.now();
        this.frameCount = 0;
        this.lastVisualUpdate = Date.now();
        this.updateStats();

        // Otomatik frame yakalama başlat
        const settings = this.settings$.value;
        if (settings.autoCapture) {
            this.remoteCamera.startAutoCapture(1000 / settings.captureInterval);
        }

        // console.log('[CMM] Tarama başladı:', name);
        return group;
    }

    /**
     * Taramayı durdur
     */
    public stopScan(): void {
        this.remoteCamera.stopAutoCapture();

        // Son bir görsel güncelleme yap
        this.updateLivePointCloud();

        this.state$.next('connected');
        // console.log('[CMM] Tarama durduruldu');
    }

    /**
     * Taramayı tamamla
     */
    public async completeScan(): Promise<CMMScanResult | null> {
        this.stopScan();
        this.state$.next('processing');

        const group = this.activeGroup$.value;
        if (!group) {
            this.state$.next('connected');
            return null;
        }

        try {
            // Canlı point cloud'u kaldır
            if (this.livePoints) {
                group.remove(this.livePoints);
                this.livePoints.geometry.dispose();
                (this.livePoints.material as THREE.Material).dispose();
                this.livePoints = null;
                this.liveGeometry = null;
            }

            // Point cloud'u işle
            const settings = this.settings$.value;

            // 1. Önce outlier'ları kaldır (şelale efektini temizler)
            // console.log('[CMM] Outlier filtreleme başlıyor...');
            this.pointCloud.removeOutliers(15, 1.5); // k=15 komşu, 1.5 std sapma

            // 2. Sonra voxel grid ile yoğunluğu azalt
            this.pointCloud.voxelGridFilter(settings.voxelSize);

            // Point cloud mesh oluştur
            const geometry = this.pointCloud.createGeometry();
            const material = new THREE.PointsMaterial({
                size: 2,
                vertexColors: true,
                sizeAttenuation: true
            });

            const points = new THREE.Points(geometry, material);
            points.name = '__scan_points__';

            // Gruba ekle
            group.addPointCloud(points);

            // Sonuç oluştur
            const result: CMMScanResult = {
                id: group.userData.fikoTarama.scanId,
                name: group.name,
                timestamp: new Date(),
                frameCount: this.frameCount,
                pointCloud: points,
                pointCloudData: this.pointCloud.getData(),
                boundingBox: geometry.boundingBox,
                duration: Date.now() - this.scanStartTime
            };

            // Sonuçlar listesine ekle
            const results = this.results$.value;
            results.push(result);
            this.results$.next(results);

            this.state$.next('completed');
            // console.log('[CMM] Tarama tamamlandı:', result.name);

            return result;

        } catch (error: any) {
            this.error$.next(`Tarama tamamlanamadı: ${error.message}`);
            this.state$.next('connected');
            return null;
        }
    }

    /**
     * Frame işle
     */
    private processFrame(frame: ImageData): void {
        this.frameCount++;

        const settings = this.settings$.value;

        // İlk 5 frame için debug log
        if (this.frameCount <= 5) {
            // console.log(`[CMM] Frame #${this.frameCount} işleniyor - ${frame.width}x${frame.height}, mod: ${settings.mode}`);
        }

        try {
            // Marker tabanlı mod
            if (settings.mode === 'marker') {
                // Marker algıla
                const markers = this.markerDetector.detect(frame);

                // Debug: Her 10 frame'de bir log
                if (this.frameCount % 10 === 1) {
                    // console.log(`[CMM] Frame #${this.frameCount}: ${markers.length} marker, referans: ${this.referencepose ? 'VAR' : 'YOK'}`);
                }

                if (markers.length > 0) {
                    // Kamera pozunu hesapla
                    const pose = this.markerDetector.estimatePose(
                        markers[0],
                        settings.markerSize,
                        { fx: 1000, fy: 1000, cx: frame.width / 2, cy: frame.height / 2 }
                    );

                    if (pose) {
                        // İlk başarılı poz için detaylı log
                        if (!this.referencepose) {
                            // console.log(`[CMM] İLK POZ ALGILANDI! Derinlik: ${pose.translation.z.toFixed(1)}mm`);
                        }

                        // ========== VIO VISUAL OBSERVATION ==========
                        // Marker algılandığında VIO'ya visual gözlem gönder
                        // Bu, IMU drift'ini düzeltmek için kritik
                        if (this.vioEnabled) {
                            const rotationMatrix = pose.rotationMatrix || new THREE.Matrix4().makeRotationFromEuler(pose.rotation);
                            const visualObs: VisualObservation = {
                                position: pose.translation.clone(),
                                rotation: pose.rotation.clone(),
                                rotationMatrix: rotationMatrix,
                                confidence: markers[0].confidence || 0.8, // Marker confidence
                                timestamp: Date.now()
                            };
                            this.vio.addVisualObservation(visualObs);
                        }

                        // Kamera hareketi hesapla
                        if (this.lastPose) {
                            const dx = pose.translation.x - this.lastPose.translation.x;
                            const dy = pose.translation.y - this.lastPose.translation.y;
                            const dz = pose.translation.z - this.lastPose.translation.z;
                            const motion = Math.sqrt(dx * dx + dy * dy + dz * dz);

                            if (motion > this.motionThreshold) {
                                // Anlamlı hareket var
                                this.totalMotion += motion;
                                this.stationaryFrames = 0;
                            } else {
                                // Hareketsiz
                                this.stationaryFrames++;
                            }
                        }
                        this.lastPose = {
                            rotation: pose.rotation.clone(),
                            translation: pose.translation.clone()
                        };

                        // ========== SfM - STRUCTURE FROM MOTION ==========
                        // Gerçek 3D noktalar triangulation ile
                        if (this.sfmEnabled) {
                            const rotationMatrix = pose.rotationMatrix || new THREE.Matrix4().makeRotationFromEuler(pose.rotation);
                            const sfmPoints = this.sfm.processFrame(frame, {
                                rotation: rotationMatrix,
                                translation: pose.translation.clone()
                            });

                            // SfM noktalarını point cloud'a ekle
                            for (const sfmPoint of sfmPoints) {
                                this.pointCloud.addPoint(sfmPoint.position, sfmPoint.color);
                            }

                            // SfM stats logla
                            if (this.frameCount % 30 === 0) {
                                const sfmStats = this.sfm.getStats();
                                // console.log(`[SfM] Features: ${sfmStats.trackedFeatures}, 3D Points: ${sfmStats.triangulatedPoints}`);
                            }
                        }

                        // Ek olarak depth estimation da kullan (daha yoğun point cloud için)
                        const depthMap = this.depthEstimator.estimate(frame);

                        // 3D noktalar oluştur ve point cloud'a ekle
                        this.addPointsFromFrame(frame, depthMap, pose);
                    } else {
                        console.warn(`[CMM] Marker bulundu ama poz hesaplanamadı!`);
                    }

                    // İstatistik güncelle
                    const stats = this.stats$.value;
                    stats.markersDetected = markers.length;
                    this.stats$.next(stats);
                } else {
                    // Marker bulunamadı - KÜRE OLUŞUMUNU ÖNLEMEK İÇİN:
                    // Sahte depth estimation noktaları EKLEME!
                    // Sadece SfM triangulated noktaları güvenilir.

                    if (this.frameCount % 30 === 0) {
                        if (this.referencepose) {
                            // console.log(`[CMM] Frame #${this.frameCount}: Marker yok, sahte depth noktaları atlanıyor (küre önleme)`);
                        } else {
                            console.warn(`[CMM] Frame #${this.frameCount}: Marker bulunamadı, referans poz yok!`);
                        }
                    }

                    // SfM yine de çalışabilir - feature tracking marker'sız da yapılabilir
                    // Ama doğru poz olmadan triangulation güvenilir olmaz
                    // Bu yüzden marker görünene kadar bekliyoruz
                }
            }
            // Markerless mod
            else {
                // Sadece depth estimation ile çalış
                const depthMap = this.depthEstimator.estimate(frame);
                const beforeCount = this.pointCloud.getPointCount();
                this.addPointsFromFrame(frame, depthMap, null);
                const afterCount = this.pointCloud.getPointCount();

                if (this.frameCount <= 5) {
                    // console.log(`[CMM] Markerless: ${afterCount - beforeCount} nokta eklendi, toplam: ${afterCount}`);
                }
            }

            this.updateStats();

        } catch (error) {
            console.error('[CMM] Frame işleme hatası:', error);
        }
    }

    /**
     * Frame'den point cloud'a nokta ekle
     *
     * DOĞRU YAKLAŞIM: Inverse Transformation
     * =========================================
     *
     * estimatePose() marker→camera transformation verir:
     *   P_camera = R * P_marker + T
     *
     * Biz camera→marker (world) istiyoruz:
     *   P_marker = R^(-1) * (P_camera - T)
     *            = R^T * P_camera - R^T * T
     *
     * Marker = World Origin (0,0,0)
     * Tüm noktalar marker koordinat sisteminde birikir
     *
     * KÜRE ÖNLEME: Sadece gerçek marker görünüyorken ve yeterli hareket varken
     * depth estimation noktaları eklenir. Aksi halde sadece SfM triangulated
     * noktalar kullanılır.
     */
    private addPointsFromFrame(
        frame: ImageData,
        depthMap: Float32Array,
        pose: { rotation: THREE.Euler; translation: THREE.Vector3; rotationMatrix?: THREE.Matrix4 } | null
    ): void {
        const width = frame.width;
        const height = frame.height;

        // Kamera parametreleri (varsayılan - telefon kamerası için yaklaşık değerler)
        const fx = 1000;
        const fy = 1000;
        const cx = width / 2;
        const cy = height / 2;

        // KÜRE ÖNLEME: Gerçek marker pozu gerekli, referans poz yeterli değil
        const isRealMarkerPose = pose !== null && pose !== this.referencepose;

        if (!pose) {
            // Poz yoksa referans kullan - AMA NOKTA EKLEME (küre oluşur)
            if (this.referencepose) {
                // Referans poz var ama marker görünmüyor
                // Bu durumda depth estimation noktaları EKLEMEYECEĞIZ
                if (this.frameCount % 60 === 1) {
                    // console.log(`[CMM] Marker görünmüyor, depth noktaları atlanıyor (küre önleme)`);
                }
                return; // ÇIKIŞ - sahte noktalar ekleme
            } else {
                return; // Hiç poz yok, çıkış
            }
        }

        // KÜRE ÖNLEME: Yeterli hareket yoksa da noktaları seyrekleştir
        const hasEnoughMotion = this.totalMotion > 20; // En az 20mm hareket
        const step = hasEnoughMotion ? 4 : 8; // Hareket yoksa daha seyrek örnekle

        // İlk poz için referans kaydet
        if (!this.referencepose) {
            this.referencepose = {
                rotation: pose.rotation.clone(),
                translation: pose.translation.clone()
            };
            // console.log('[CMM] ✓ Referans poz ayarlandı! depth:', pose.translation.z.toFixed(1), 'mm');
            // console.log('[CMM] Artık noktalar eklenmeye başlayacak...');
        }

        // KÜRE ÖNLEME: İlk birkaç frame'de çok fazla nokta ekleme
        if (this.frameCount < 5 && !hasEnoughMotion) {
            if (this.frameCount === 1) {
                // console.log('[CMM] İlk frame - hareket bekleniyor...');
            }
            return; // İlk frame'lerde hareket yoksa bekle
        }

        // ========== VIO ENHANCED SENSOR FUSION ==========
        // VIO varsa: 6-DoF pose (Madgwick AHRS + IMU Preintegration + Visual Correction)
        // VIO yoksa: Basit complementary filter (visual + IMU quaternion)

        let finalOrientation: THREE.Quaternion;
        let finalPosition: THREE.Vector3;
        let markerDepth: number;

        if (this.vioEnabled && this.lastVIOPose && this.lastVIOPose.confidence > 0.3) {
            // VIO modunda - profesyonel 6-DoF pose kullan
            finalOrientation = this.lastVIOPose.orientation.clone();
            finalPosition = this.lastVIOPose.position.clone();

            // VIO pozisyonunu marker derinliği ile birleştir
            // VIO pozisyon: drift olabilir, marker: doğru ama kesikli
            // Marker görünüyorsa marker depth kullan, yoksa VIO'dan hesapla
            if (pose !== this.referencepose) {
                // Marker şu an görünüyor - marker depth kullan
                markerDepth = pose.translation.z;

                // VIO pozisyonunu marker ile senkronize et
                // Bu drift'i düzeltir
                const visualWeight = 0.7;
                finalPosition.lerp(pose.translation, visualWeight);
            } else {
                // Marker görünmüyor - VIO depth kullan
                markerDepth = Math.max(200, finalPosition.z);
            }

            // Debug: VIO kullanımını logla
            if (this.frameCount % 60 === 0) {
                // console.log(`[VIO] Fusion aktif - conf=${(this.lastVIOPose.confidence * 100).toFixed(0)}%, depth=${markerDepth.toFixed(0)}mm`);
            }
        } else {
            // Fallback: Eski basit fusion
            const fusedRotation = this.fuseWithIMU(pose.rotation);
            finalOrientation = new THREE.Quaternion().setFromEuler(fusedRotation);
            finalPosition = pose.translation.clone();
            markerDepth = pose.translation.z;
        }

        // ========== INVERSE TRANSFORMATION HESAPLA ==========
        // solvePnP: P_cam = R * P_marker + T
        // Inverse:  P_marker = R^T * (P_cam - T)

        // Forward rotation matrix (marker→camera) - VIO/fused orientation kullan
        const R = new THREE.Matrix4().makeRotationFromQuaternion(finalOrientation);

        // Inverse rotation matrix R^T (camera→marker)
        const R_inv = R.clone().transpose();

        // Translation vector T - VIO/fused position kullan
        const T = finalPosition.clone();

        // Inverse translation: -R^T * T
        const T_inv = T.clone().negate();
        T_inv.applyMatrix4(R_inv);

        // Toplanan nokta sayısı
        let addedPoints = 0;

        for (let y = 0; y < height; y += step) {
            for (let x = 0; x < width; x += step) {
                // Merkez bölge - marker görünür alanda olmalı
                // Sadece görüntünün merkez %80'ini işle (kenarlar distorsiyon içerir)
                const marginX = width * 0.1;
                const marginY = height * 0.1;
                if (x < marginX || x > width - marginX || y < marginY || y > height - marginY) {
                    continue;
                }

                // Depth map'ten yüzey varyasyonu al
                const idx = y * width + x;
                const rawDepth = depthMap[idx];

                // ========== GELİŞTİRİLMİŞ DERİNLİK TAHMİNİ ==========
                // Monoküler kameradan gerçek derinlik alamayız, ama:
                // 1. Marker derinliği doğru (PnP'den)
                // 2. Texture/gradient bilgisi yüzey varyasyonu ipucu verir
                // 3. Hareket varsa parallax'tan depth farkı tahmin edilebilir

                const normalizedDepth = (rawDepth - 200) / 800; // 0-1 arası normalize
                const clampedDepth = Math.max(0, Math.min(1, normalizedDepth));

                // Varyasyon: marker depth'in %30'una kadar (daha anlamlı 3D yapı)
                // Yüksek gradient = kenar/yakın, düşük gradient = düz/orta
                const maxVariation = markerDepth * 0.3; // ±30% varyasyon
                const surfaceVariation = (0.5 - clampedDepth) * maxVariation * 2;

                // Kamera hareket ediyorsa, daha güvenilir depth farkı
                const motionFactor = Math.min(1, this.totalMotion / 100); // 100mm hareket = full confidence
                const adjustedVariation = surfaceVariation * (0.3 + 0.7 * motionFactor);

                const depth = markerDepth + adjustedVariation;

                // Geçersiz derinlik kontrolü
                if (depth <= 100 || depth > 3000) continue;

                // ========== CAMERA COORDINATES ==========
                // Pinhole camera model: 3D → 2D projection
                // x_pixel = fx * X_cam / Z_cam + cx
                // Inverse: X_cam = (x_pixel - cx) * Z_cam / fx
                const P_cam = new THREE.Vector3(
                    (x - cx) * depth / fx,
                    (y - cy) * depth / fy,
                    depth
                );

                // ========== WORLD (MARKER) COORDINATES ==========
                // P_marker = R^T * (P_cam - T)
                //          = R^T * P_cam + T_inv

                // 1. R^T * P_cam
                const P_marker = P_cam.clone();
                P_marker.applyMatrix4(R_inv);

                // 2. + T_inv (= -R^T * T)
                P_marker.add(T_inv);

                // Aşırı uzak noktaları filtrele (marker'dan 500mm'den fazla uzakta)
                if (P_marker.length() > 500) continue;

                // Renk al
                const pixelIdx = (y * width + x) * 4;
                const r = frame.data[pixelIdx] / 255;
                const g = frame.data[pixelIdx + 1] / 255;
                const b = frame.data[pixelIdx + 2] / 255;

                // Point cloud'a ekle
                this.pointCloud.addPoint(P_marker, new THREE.Color(r, g, b));
                addedPoints++;
            }
        }

        // Debug: İlk 5 frame için log
        if (this.frameCount <= 5) {
            // console.log(`[CMM] Frame #${this.frameCount}: ${addedPoints} nokta eklendi, depth=${markerDepth.toFixed(0)}mm`);
        }
    }

    /**
     * İstatistikleri güncelle
     */
    private updateStats(): void {
        const stats: CMMStats = {
            framesCaptured: this.frameCount,
            pointsCollected: this.pointCloud.getPointCount(),
            markersDetected: this.stats$.value.markersDetected,
            scanDuration: Date.now() - this.scanStartTime,
            lastFrameTime: Date.now()
        };
        this.stats$.next(stats);

        // Canlı görselleştirmeyi güncelle (belirli aralıklarla)
        const now = Date.now();
        if (now - this.lastVisualUpdate >= this.visualUpdateInterval) {
            this.updateLivePointCloud();
            this.lastVisualUpdate = now;
        }

        // Mobil'e durum gönder (her 10 frame'de bir)
        if (this.frameCount % 10 === 0) {
            this.sendStatusToMobile(stats);
        }
    }

    /**
     * Canlı point cloud oluştur
     */
    private createLivePointCloud(group: FikoCanlıTaramaGrup): void {
        // Eski point cloud varsa temizle
        if (this.livePoints) {
            this.livePoints.geometry.dispose();
            (this.livePoints.material as THREE.Material).dispose();
            this.livePoints.parent?.remove(this.livePoints);
        }

        // Boş geometry oluştur (dinamik olarak güncellenecek)
        this.liveGeometry = new THREE.BufferGeometry();

        // Başlangıç için boş pozisyon ve renk attribute'ları
        const positions = new Float32Array(0);
        const colors = new Float32Array(0);

        this.liveGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.liveGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        // Material
        const material = new THREE.PointsMaterial({
            size: 3,
            vertexColors: true,
            sizeAttenuation: true,
            transparent: true,
            opacity: 0.8
        });

        // Points mesh
        this.livePoints = new THREE.Points(this.liveGeometry, material);
        this.livePoints.name = '__live_scan_points__';

        // Gruba ekle
        group.add(this.livePoints);

        // console.log('[CMM] Canlı point cloud oluşturuldu');
    }

    /**
     * Canlı point cloud güncelle
     */
    private updateLivePointCloud(): void {
        if (!this.livePoints || !this.liveGeometry) return;

        const pointCount = this.pointCloud.getPointCount();
        if (pointCount === 0) return;

        // Point cloud verilerini al
        const data = this.pointCloud.getData();
        if (!data) return;

        // Pozisyon ve renk array'lerini oluştur
        const positions = new Float32Array(pointCount * 3);
        const colors = new Float32Array(pointCount * 3);

        for (let i = 0; i < pointCount; i++) {
            positions[i * 3] = data.positions[i * 3];
            positions[i * 3 + 1] = data.positions[i * 3 + 1];
            positions[i * 3 + 2] = data.positions[i * 3 + 2];

            colors[i * 3] = data.colors[i * 3];
            colors[i * 3 + 1] = data.colors[i * 3 + 1];
            colors[i * 3 + 2] = data.colors[i * 3 + 2];
        }

        // Geometry güncelle
        this.liveGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.liveGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        this.liveGeometry.attributes.position.needsUpdate = true;
        this.liveGeometry.attributes.color.needsUpdate = true;
        this.liveGeometry.computeBoundingSphere();

        // Debug log (ilk birkaç güncelleme için)
        if (this.frameCount <= 15 && this.frameCount % 5 === 0) {
            // console.log(`[CMM] Canlı görselleştirme güncellendi: ${pointCount} nokta`);
        }
    }

    /**
     * Mobil'e durum gönder
     */
    private sendStatusToMobile(stats: CMMStats): void {
        const socket = (this.remoteCamera as any).socket;
        const sessionId = (this.remoteCamera as any).sessionId;

        if (socket && sessionId) {
            // Durum mesajı oluştur
            let status = 'Marker aranıyor...';
            if (stats.markersDetected > 0) {
                if (this.stationaryFrames > 30) {
                    status = '⚠️ Kamerayı hareket ettirin!';
                } else if (this.totalMotion > 50) {
                    status = '✅ İyi gidiyor, hareket edin';
                } else {
                    status = '📱 Kamerayı yavaşça hareket ettirin';
                }
            }

            // VIO ve SfM debug bilgisi
            const vioDebug = this.vio.getDebugInfo();
            const sfmStats = this.sfm.getStats();

            socket.emit('FikoYT.cmm.scan.status', {
                sessionId: sessionId,
                pointCount: stats.pointsCollected,
                frameCount: stats.framesCaptured,
                markersDetected: stats.markersDetected,
                status: status,
                scanning: this.state$.value === 'scanning',
                totalMotion: this.totalMotion,
                imuActive: this.imuCalibrated && this.lastIMUData !== null,
                sensorFusion: this.imuCalibrated,
                // VIO bilgileri
                vioEnabled: this.vioEnabled,
                vioCalibrated: vioDebug.isCalibrated,
                vioReferenceSet: vioDebug.referenceSet,
                vioDrift: vioDebug.drift,
                vioConfidence: this.lastVIOPose?.confidence || 0,
                // SfM bilgileri
                sfmEnabled: this.sfmEnabled,
                sfmTrackedFeatures: sfmStats.trackedFeatures,
                sfmTriangulatedPoints: sfmStats.triangulatedPoints
            });
        }
    }

    /**
     * Ayarları güncelle
     */
    public updateSettings(settings: Partial<CMMSettings>): void {
        const current = this.settings$.value;
        this.settings$.next({ ...current, ...settings });
    }

    // ========== POINT CLOUD IMPORT ==========

    /**
     * Desteklenen dosya formatları
     * FikoImporterPointCloud ile senkronize
     */
    public static readonly SUPPORTED_FORMATS = POINT_CLOUD_FORMATS;

    /**
     * Point cloud dosyası import et
     * FikoImporterPointCloud kullanılır - kod tekrarı önlendi
     */
    public async importPointCloud(file: File): Promise<FikoPointCloudGrup | null> {
        if (!this.scene) {
            this.error$.next('Sahne ayarlanmamış');
            return null;
        }

        // console.log(`[CMM] Import başlıyor: ${file.name}`);

        // FikoImporterPointCloud kullan
        const importer = new FikoImporterPointCloud(this.scene as any);

        // Progress callback
        importer.onProgress = (mesaj: string, progress: number) => {
            // console.log(`[CMM] ${mesaj} (${progress}%)`);
        };

        try {
            const group = await importer.yukle(file);

            if (group) {
                // Ürün ağacına ekle (3D çizimler grubuna)
                const urunAgaci = (this.scene as any).urunAgaci;
                if (urunAgaci) {
                    // "Fiko Çizimler" grubunu bul
                    let cizimGrup = urunAgaci.children?.find((c: any) => c.name === 'Fiko Çizimler');
                    if (cizimGrup) {
                        cizimGrup.add(group);
                        // console.log('[CMM] Point Cloud "Fiko Çizimler"e eklendi:', group.name);
                    } else {
                        urunAgaci.add(group);
                    }
                } else {
                    this.scene.add(group);
                }

                // Sonuçlara ekle
                const geometry = group.children.find(c => c instanceof THREE.Points)?.geometry as THREE.BufferGeometry;
                const result: CMMScanResult = {
                    id: `import_${Date.now()}`,
                    name: group.name,
                    timestamp: new Date(),
                    frameCount: 1,
                    pointCloud: group.children.find(c => c instanceof THREE.Points) as THREE.Points,
                    pointCloudData: group.pointCloudData,
                    boundingBox: geometry?.boundingBox || null,
                    duration: 0
                };

                const results = this.results$.value;
                results.push(result);
                this.results$.next(results);
            }

            return group;

        } catch (error: any) {
            this.error$.next(`Import hatası: ${error.message}`);
            return null;
        }
    }

    /**
     * Bağlantıyı kes
     */
    public disconnect(): void {
        this.remoteCamera.disconnect();
        this.state$.next('idle');
    }

    /**
     * Temizle
     */
    public dispose(): void {
        // Canlı point cloud temizle
        if (this.livePoints) {
            this.livePoints.geometry.dispose();
            (this.livePoints.material as THREE.Material).dispose();
            this.livePoints.parent?.remove(this.livePoints);
            this.livePoints = null;
            this.liveGeometry = null;
        }

        this.subscriptions.forEach(s => s.unsubscribe());
        this.remoteCamera.dispose();
        this.pointCloud.clear();
        this.markerDetector.dispose();
        this.depthEstimator.dispose();
        this.sfm.clear();

        this.state$.complete();
        this.settings$.complete();
        this.stats$.complete();
        this.results$.complete();
        this.activeGroup$.complete();
        this.connectionState$.complete();
        this.pairingInfo$.complete();
        this.error$.complete();
    }
}
